import Canvas, {EVENT_DRAWING, EVENT_HISTORY_CHANGED} from "ui/canvas/Canvas";
import {IS_HIGH_DPR} from "ui/config";
import EventDispatcher from "ui/EventDispatcher";
import Ajax from "ui/helpers/Ajax";

export const EVENT_TAKE_FRAME = 'take_frame';
const VIDEO_WIDTH = 1080;
const VIDEO_HEIGHT = 810;
const FRAMES_MIN_TIME_GAP = 330; // ms

export class SceneEditorVideoRecorder {
	/**
	 * @param {Canvas} canvas 
	 */
	constructor(canvas) {
		/**
		 * @type {Canvas}
		 * @private
		 */
		this._canvas = canvas;

		/** @private */
		this._isRecording = false;
		
		/** @private */
		this._isBusy = false;
		
		/** @private */
		this._framesCount = 0;

		/** @private */
		this._sequencesCount = 0;

		/** @private */
		this._filesSize = 0;

		/** @private */
		this._duration = 0;

		/** @private */
		this._lastTakeTime = 0;
		
		/** @private */
		this._events = new EventDispatcher();

		/** @private */
		this._deferredCallTimeout = 0;

		/** 
		 * @private 
		 * @type {HTMLCanvasElement}
		 */
		this._workCanvas = document.createElement('canvas');
		this._workCanvas.width = VIDEO_WIDTH;
		this._workCanvas.height = VIDEO_HEIGHT;
		
		/** 
		 * @private 
		 * @type {CanvasRenderingContext2D}
		 */
		this._workCtx = this._workCanvas.getContext('2d');

		/** @private */
		this._handlers = {
			takeFrame: this._takeFrame.bind(this)
		};

		canvas.events.addListener(EVENT_HISTORY_CHANGED, this._handlers.takeFrame);
		canvas.events.addListener(EVENT_DRAWING, this._handlers.takeFrame);
	}

	/** @return {EventDispatcher} */
	get events() {
		return this._events;
	}

	/** @return {number} */
	get framesCount() {
		return this._framesCount;
	}

	/** @return {number} */
	get sequencesCount() {
		return this._sequencesCount
	}

	/** @return {number} */
	get filesSize() {
		return this._filesSize;
	}

	/** @return {number} */
	get duration() {
		return this._duration;
	}

	async start() {
		if (this._isRecording) {
			return false;
		}

		try {
			const resp = await Ajax.post('/video/start-recording');
			
			this._framesCount = resp.frames_count;
			this._sequencesCount = resp.sequences_count;
			this._filesSize = resp.files_size;
			this._duration = resp.duration;
		} catch (e) {
			return false;
		}
		
		this._isRecording = true;
		return true;
	}

	stop() {
		if (!this._isRecording) {
			return;
		}

		this._isRecording = false;
	}

	/** @private */
	async _takeFrame() {
		if (this._isBusy || !this._isRecording) {
			return;
		}

		const now = Date.now();
		clearTimeout(this._deferredCallTimeout);

		if (now - this._lastTakeTime < FRAMES_MIN_TIME_GAP) {
			this._deferredCallTimeout = setTimeout(this._handlers.takeFrame, FRAMES_MIN_TIME_GAP);
			return;
		}
		
		this._lastTakeTime = now;

		this._isBusy = true;
		const src = this._canvas.displayCanvas;
		const footerPadding = 200 * (IS_HIGH_DPR ? 2 : 1);
		const coef = Math.min(src.width / VIDEO_WIDTH, (src.height - footerPadding) / VIDEO_HEIGHT);
		const fromWidth = Math.ceil(VIDEO_WIDTH * coef);
		const fromHeight = Math.ceil(VIDEO_HEIGHT * coef);
		this._workCtx.fillStyle = '#888';
		this._workCtx.fillRect(0, 0, VIDEO_WIDTH, VIDEO_HEIGHT);
		this._workCtx.drawImage(
			src, 
			(src.width >> 1) - (fromWidth >> 1),
			((src.height - footerPadding) >> 1) - (fromHeight >> 1),
			fromWidth,
			fromHeight,
			0, 
			0,
			VIDEO_WIDTH,
			VIDEO_HEIGHT
		);

		queueMicrotask(async () => {
			const imgData = this._workCanvas.toDataURL('image/jpeg', 80);

			try {
				const resp = await Ajax.post('/video/save-frame', {
					'img': imgData.slice(imgData.indexOf(',') + 1),
				});
				
				this._framesCount = resp.frames_count;
				this._sequencesCount = resp.sequences_count;
				this._filesSize = resp.files_size;
				this._duration = resp.duration;
			} finally {
				this._isBusy = false;	
			}

			this._events.trigger(EVENT_TAKE_FRAME, this._frames);
		});
	}
}